import React from "react";
import Markdown from "markdown-to-jsx";
import { makeStyles } from "@material-ui/core/styles";

const elements = [
  "a",
  "abbr",
  "address",
  "area",
  "article",
  "aside",
  "audio",
  "b",
  "base",
  "bdi",
  "bdo",
  "big",
  "blockquote",
  "body",
  "br",
  "button",
  "canvas",
  "caption",
  "cite",
  "code",
  "col",
  "colgroup",
  "data",
  "datalist",
  "dd",
  "del",
  "details",
  "dfn",
  "dialog",
  "div",
  "dl",
  "dt",
  "em",
  "embed",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "head",
  "header",
  "hgroup",
  "hr",
  "html",
  "i",
  "iframe",
  "img",
  "input",
  "ins",
  "kbd",
  "keygen",
  "label",
  "legend",
  "li",
  "link",
  "main",
  "map",
  "mark",
  "marquee",
  "menu",
  "menuitem",
  "meta",
  "meter",
  "nav",
  "noscript",
  "object",
  "ol",
  "optgroup",
  "option",
  "output",
  "p",
  "param",
  "picture",
  "pre",
  "progress",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "script",
  "section",
  "select",
  "small",
  "source",
  "span",
  "strong",
  "style",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "textarea",
  "tfoot",
  "th",
  "thead",
  "time",
  "title",
  "tr",
  "track",
  "u",
  "ul",
  "var",
  "video",
  "wbr",

  // SVG
  "circle",
  "clipPath",
  "defs",
  "ellipse",
  "foreignObject",
  "g",
  "image",
  "line",
  "linearGradient",
  "marker",
  "mask",
  "path",
  "pattern",
  "polygon",
  "polyline",
  "radialGradient",
  "rect",
  "stop",
  "svg",
  "text",
  "tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

// --------------- PATRICK ------------------------
const useStyles = makeStyles((theme) => ({
  msg: {
    color: theme.mode === "light" ? "#8A2BE2" : "#32CD32", // Corrigido: theme.palette.mode
    textDecoration: "underline",
    fontWeight: "bold",
  },
}));
// -----------------------------------------------
const CustomLink = ({ children, href, ...props }) => {
  const classes = useStyles();
  if (href && href.startsWith("mention://")) {
    return (
      <span style={{ color: "#1976d2", fontWeight: 600 }}>
        {children}
      </span>
    );
  }
  return (
    <a
      {...props}
      href={href}
      className={classes.msg}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};
// -----------------------------------------------
// const CustomLink = ({ children, ...props }) => (
//   <a {...props} target="_blank" rel="noopener noreferrer">
//     {children}
//   </a>
// );

const MarkdownWrapper = ({ children }) => {
  const boldRegex = /\*(.*?)\*/g;
  const tildaRegex = /~(.*?)~/g;

  // --------------- PATRICK ------------------------
  // 1) URLs completas com protocolo (ex: https://drive.google.com/...) - como WhatsApp
  const fullUrlRegex = /(https?:\/\/[^\s]+)/gi;
  // 2) URLs sem protocolo (www. ou domínio.tld) para não quebrar links já tratados
  const urlRegex =
    /\b((https?:\/\/)?(www\.)?([\w-]+\.)*[\w-]+\.(com(?:\.br)?|net(?:\.br)?|org(?:\.br)?|io|dev)(\/\S*)?)\b/gi;
  // -----------------------------------------------
  const pixMarker = "###PIX_MARKER###";

  if (children && children.includes("***")) {
    children = children.replace(/\*\*\*/g, pixMarker);
  }

  if (children && boldRegex.test(children)) {
    children = children.replace(boldRegex, "**$1**");
  }

  // Destaque de menções @Nome — processado ANTES das URLs para não conflitar
  // (?<!\w) evita capturar @ em e-mails como user@domain.com
  const mentionRegex = /(?<!\w)@([A-Za-zÀ-ɏḀ-ỿ\w]+(?:[ ][A-Za-zÀ-ɏḀ-ỿ\w]+)?)/g;
  if (children) {
    mentionRegex.lastIndex = 0;
    children = children.replace(mentionRegex, (_match, name) => {
      return `[@${name}](mention://${encodeURIComponent(name)})`;
    });
  }

  // --------------- PATRICK ------------------------
  // URLs completas (https:// ou http://) — estilo WhatsApp; uma única passada evita quebrar drive.google.com etc.
  if (children && fullUrlRegex.test(children)) {
    children = children.replace(fullUrlRegex, (url) => {
      const trimmed = url.replace(/[.,;:!?)]+$/, ""); // não incluir pontuação final no link
      return `[${trimmed}](${trimmed})`;
    });
  } else if (children && urlRegex.test(children)) {
    // Só trata www. ou exemplo.com quando não há URL com protocolo (evita reprocessar e quebrar)
    children = children.replace(urlRegex, (url) => {
      const fullUrl = url.startsWith("http") ? url : `https://${url}`;
      return `[${url}](${fullUrl})`;
    });
  }
  // -----------------------------------------------
  if (children && children.includes(pixMarker)) {
    children = children.replace(new RegExp(pixMarker, "g"), "***");
  }

  if (children && tildaRegex.test(children)) {
    children = children.replace(tildaRegex, "~~$1~~");
  }
  const options = React.useMemo(() => {
    const markdownOptions = {
      disableParsingRawHTML: true,
      forceInline: true,
      overrides: {
        a: { component: CustomLink },
      },
    };

    elements.forEach((element) => {
      if (!allowedElements.includes(element)) {
        markdownOptions.overrides[element] = (el) => el.children || null;
      }
    });

    return markdownOptions;
  }, []);

  if (!children) return null;

  return <Markdown options={options}>{children}</Markdown>;
};

export default MarkdownWrapper;
